<template>
	<CardHeader
		:class="cn(condoCardHeaderBodyVariants({ size, variant }), props.class)"
	>
		<CardCarousel
			v-bind="forwardedProps"
			:images="condo!.images"
			:dots-variants="condoCardCarouselDotsVariants"
			@image-clicked="handlePropertyClicked"
			@image-scrolled="handlePropertyImageScrolled"
		>
			<template #default="{ image, index }">
				<CardCarouselImage
					:index="index"
					:image="image"
					:variant="variant"
					:image-alt="getImageAlt(index)"
					:is-property-active="true"
				/>
			</template>
		</CardCarousel>

		<div class="absolute top-0 z-20 flex w-full items-center gap-2 p-4"></div>

		<slot />
	</CardHeader>
</template>

<script setup lang="ts">
import type { CondoCardHeaderProps } from '.';
import type {
	ImageClickedEvent,
	ImageScrolledEvent
} from '../Card/CardCarousel';
import type { PropertyCardImageScrolled } from '~/types/mixpanel';

import { cn } from '@/utils';
import { useEvents } from '~/composables/events/useEvents';
import { MixpanelProps } from '~/core/service/mixpanel';
import { isNumber } from '~~/utils';
import { CONDO_CARD } from '~~/utils/symbols';
import { useForwardProps } from 'reka-ui';

import { condoCardCarouselDotsVariants, condoCardHeaderBodyVariants } from '.';
import { CardCarousel } from '../Card/CardCarousel';
import { CardCarouselImage } from '../Card/CardCarouselImage';
import { CardHeader } from '../ui/card';

const props = defineProps<CondoCardHeaderProps>();

const condo = inject(CONDO_CARD)!;
const route = useRoute();
const { trackCondoCardClicked, trackPropertyImageScrolled } = useEvents();

const delegatedProps = computed(() => {
	const { class: _, ...delegated } = props;

	return delegated;
});

const forwardedProps = useForwardProps(delegatedProps);

const trackingSharedProps = computed<
	Omit<PropertyCardImageScrolled, typeof MixpanelProps.CLICK_COUNT>
>(() => {
	const cardPosition = isNumber(props.cardPosition)
		? props.cardPosition
		: undefined;

	return {
		[MixpanelProps.COMMERCIAL_ID]: condo.name,
		[MixpanelProps.CURRENT_PAGE]: route.fullPath,
		[MixpanelProps.SECTION_NAME]: props.section || '',
		[MixpanelProps.CARD_POSITION]: cardPosition,
		[MixpanelProps.CARD_TYPE]: 'regular_card',
		[MixpanelProps.COMPANY_NAME]: '',
		[MixpanelProps.TAG_EXCLUSIVE]: false,
		[MixpanelProps.AGENT_NAME]: ''
	};
});

function handlePropertyClicked(event: ImageClickedEvent) {
	trackCondoCardClicked({
		[MixpanelProps.DESTINATION_URL]: `/condominio/${condo.slug}`,
		[MixpanelProps.PHOTO_URL]: event.photoUrl,
		[MixpanelProps.PHOTO_POSITION]: event.photoPosition,
		...trackingSharedProps.value
	});
}

function handlePropertyImageScrolled(event: ImageScrolledEvent) {
	trackPropertyImageScrolled({
		[MixpanelProps.CLICK_COUNT]: event.clickCount,
		...trackingSharedProps.value
	});
}

function getImageAlt(index: number) {
	const condoName = condo.name;
	const position = index + 1;

	return position === 1 ? condoName : `${condoName} Imagem ${position}`;
}
</script>
